import { logger } from '../useSessionRecordingStore/lib/logger';
import { dbV2 } from './dbV2';
import {
  mirrorKey,
  peekRecordingRow,
  peekUnpersistedRecordingRow,
} from './recordingRowCache';
import { recordingPk, type RecordingKey, type RecordingRow } from './types';
import { isMemoryOnlyArmed } from './writeFence';

/** Which child table the write belongs to, carried into the integrity event. */
export type ChildWriteKind = 'chunk' | 'chunk-metadata' | 'backup';

export type ParentResolution =
  | { outcome: 'durable'; recording: RecordingRow }
  | { outcome: 'repaired'; recording: RecordingRow }
  | { outcome: 'memory-only-refused' }
  | { outcome: 'missing-parent-refused' };

/**
 * `stale-mirror` is a row the store already took and no longer holds — a delete
 * beat this write — which is a different fault from a recording nothing ever
 * mirrored.
 */
type ParentSource = 'unpersisted-mirror' | 'stale-mirror' | 'missing';

export const parentAccepted = (
  resolution: ParentResolution
): resolution is Extract<ParentResolution, { recording: RecordingRow }> =>
  resolution.outcome === 'durable' || resolution.outcome === 'repaired';

type ChildWriteOutcome = 'repaired' | 'refused';

// One warn per recording AND outcome: a parentless recording misses on every
// chunk it produces and the alert reads the population, not the count — but an
// earlier refusal must not swallow the repair that follows it.
const reported = new Map<string, Set<ChildWriteOutcome>>();

const report = (
  key: RecordingKey,
  childKind: ChildWriteKind,
  details: {
    parentSource: ParentSource;
    memoryOnlyArmed: boolean;
    outcome: ChildWriteOutcome;
  }
): void => {
  const id = mirrorKey(key);
  const outcomes = reported.get(id) ?? new Set<ChildWriteOutcome>();
  if (outcomes.has(details.outcome)) return;
  outcomes.add(details.outcome);
  reported.set(id, outcomes);
  // Never `logStorePersistFailure`: its memory-only branch demotes to debug,
  // and orphaned audio has to reach the alert whatever the fence is doing.
  logger.warn({
    event_type: 'LEDGER_CHILD_WRITE_INTEGRITY',
    details: { childKind, ...details },
  });
};

/**
 * Whether a child row may land, and what parent it lands under. Must run inside
 * an already-open transaction that includes `dbV2.recordings`, or the parent it
 * proves can be cascade-deleted before the child write reaches the store.
 *
 * Only a parent the store never took is written back: the mirror refused it, so
 * replaying it contradicts nothing. A mirror the store DID take survives a
 * committed delete until the post-commit forget runs, and repairing from it
 * would resurrect audio the user deleted.
 */
export const resolveParentForChildWrite = async (
  key: RecordingKey,
  childKind: ChildWriteKind
): Promise<ParentResolution> => {
  const stored = await dbV2.recordings.get(recordingPk(key));
  if (stored) return { outcome: 'durable', recording: stored };

  const repairable = peekUnpersistedRecordingRow(key);
  const parentSource: ParentSource = repairable
    ? 'unpersisted-mirror'
    : peekRecordingRow(key)
      ? 'stale-mirror'
      : 'missing';

  // Memory-only writes nothing durable, so a parent write would be refused too
  // and the retry buffer already owns this recording's audio.
  if (isMemoryOnlyArmed()) {
    report(key, childKind, { parentSource, memoryOnlyArmed: true, outcome: 'refused' });
    return { outcome: 'memory-only-refused' };
  }

  if (!repairable) {
    report(key, childKind, {
      parentSource,
      memoryOnlyArmed: false,
      outcome: 'refused',
    });
    return { outcome: 'missing-parent-refused' };
  }

  // Reported by the caller once this transaction commits: an abort would leave
  // a repair claimed that never landed.
  await dbV2.recordings.put(repairable);
  return { outcome: 'repaired', recording: repairable };
};

/** The repaired parent is durable. Refusals stay reported inline — they write nothing. */
export const reportCommittedParentRepair = (
  key: RecordingKey,
  childKind: ChildWriteKind
): void =>
  report(key, childKind, {
    parentSource: 'unpersisted-mirror',
    memoryOnlyArmed: false,
    outcome: 'repaired',
  });

/** Dropped with the recording, so a re-created one warns on its own merits. */
export const forgetParentGuardSuppression = (key: RecordingKey): void => {
  reported.delete(mirrorKey(key));
};

export const resetParentGuardSuppression = (): void => {
  reported.clear();
};
